import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { FaClock, FaLock, FaHeart, FaArrowRight, FaWhatsapp, FaCreditCard, FaFire, FaCrown } from 'react-icons/fa';
import axios from 'axios';
import { getSocialProofText, getRemainingSpotsText } from '@/lib/socialProof';
import { PERIOD_META, pickCopPrice, selectRenewalPeriods } from '@/lib/renewalPeriods';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Overlay que se muestra cuando el plan del usuario está vencido o en período de gracia.
 *
 * ACTUALIZACIÓN 2026-02:
 * - El overlay de renovación ahora muestra AMBAS opciones (Mensual y Anual con 40% OFF),
 *   igual que la pantalla inicial de suscripción de Fit Evolution.
 * - El usuario elige el periodo desde aquí y se envía al Checkout con el `period` correcto.
 */
const ANNUAL_DISCOUNT_PCT = 40; // 40% de descuento sobre 12 mensualidades

// Normaliza el periodo de suscripción a una de las 5 claves de periodo
const normalizePeriod = (p) => {
  const s = String(p || '').toLowerCase().trim();
  if (['yearly', 'annual', 'anual', 'año', 'ano', 'year'].includes(s)) return 'yearly';
  if (['bimonthly', 'bimestral'].includes(s)) return 'bimonthly';
  if (['quarterly', 'trimestral'].includes(s)) return 'quarterly';
  if (['semiannual', 'semester', 'semestral'].includes(s)) return 'semiannual';
  return 'monthly';
};

export default function PlanExpiredOverlay({
  graceDaysRemaining = 0,
  isFullyExpired = false,
  planType = 'basic',
  renewalPrice = null,
  isVipPrice = false,
  subscriptionPeriod = 'mensual',
  // Precio especial del cliente (vip_clients.fixed_price, COP) y el periodo
  // para el que la coach lo configuró (vip_clients.subscription_period).
  vipSpecialPrice = null,
  vipPeriod = null,
  onRenew,
  onPayOnline,
}) {
  const navigate = useNavigate();

  // Estado del periodo seleccionado en el overlay bloqueante.
  // Por defecto: anual (mejor oferta), igual que la pantalla inicial de suscripción.
  // Si el cliente tiene precio especial, se preselecciona su periodo.
  const vipSpecial = isVipPrice ? (Number(vipSpecialPrice) || 0) : 0;
  const vipKey = normalizePeriod(vipPeriod || subscriptionPeriod);
  const [selectedPeriod, setSelectedPeriod] = useState(vipSpecial > 0 ? vipKey : 'yearly');
  // Fallback: si el backend no envió renewalPrice (subscription_price null),
  // buscamos el precio del plan en /api/public/subscription-plans para NO
  // dejar la pantalla con $0. La primera respuesta útil se cachea.
  const [fallbackPlan, setFallbackPlan] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/subscription-plans`);
        const plans = res.data?.plans || [];
        const target = String(planType || 'basic').toLowerCase().trim();
        const p = plans.find(
          (pl) => String(pl.slug || '').toLowerCase().trim() === target
            || String(pl.id || '').toLowerCase().trim() === target
            || String(pl.id || '').toLowerCase().trim() === `plan_${target}`
        );
        if (alive && p) {
          // 2026-02 · cargar los 5 periodos. Ticket #274906: SOLO precios COP configurados
          // (sin fallback a USD: un USD mostrado como pesos daba precios absurdos, p. ej. $1.680).
          setFallbackPlan({
            ...p,
            price_monthly: pickCopPrice(p, 'price_monthly_cop', 'monthly'),
            price_bimonthly: pickCopPrice(p, 'price_bimonthly_cop', 'bimonthly'),
            price_quarterly: pickCopPrice(p, 'price_quarterly_cop', 'quarterly'),
            price_semiannual: pickCopPrice(p, 'price_semiannual_cop', 'semester'),
            price_yearly: pickCopPrice(p, 'price_yearly_cop', 'yearly'),
          });
        }
      } catch {
        // silent
      }
    };
    load();
    return () => { alive = false; };
  }, [planType]);

  // ── Cálculo de precios para los 5 periodos ──
  // Si tenemos renewalPrice (precio actual del cliente), lo usamos como ancla
  // para derivar los otros periodos; si no, caemos al fallbackPlan.
  const basePrices = useMemo(() => {
    const base = Number(renewalPrice) || 0;
    const originalPeriod = normalizePeriod(subscriptionPeriod);
    const discountFactor = 1 - ANNUAL_DISCOUNT_PCT / 100; // 0.6
    // Preferir fallbackPlan si tiene algún precio real cargado
    const fb = fallbackPlan || {};
    const fbMap = {
      monthly: Number(fb.price_monthly) || 0,
      bimonthly: Number(fb.price_bimonthly) || 0,
      quarterly: Number(fb.price_quarterly) || 0,
      semiannual: Number(fb.price_semiannual) || 0,
      yearly: Number(fb.price_yearly) || 0,
    };
    // Si fallbackPlan trae valores, los usamos tal cual.
    // Ticket #274906: un periodo en 0 NO se rellena con un valor calculado (mensual x N, o
    // mensual x 12 x 0.6): la coach no lo configuro, y mostrar un precio que ella no puso es
    // justo lo que reclamo. Ese periodo simplemente no aparece hasta que tenga precio.
    if (Object.values(fbMap).some((v) => v > 0)) {
      return fbMap;
    }
    // Fallback 2: derivar todo desde renewalPrice.
    if (base > 0) {
      let monthly;
      if (originalPeriod === 'yearly') monthly = Math.round(base / (12 * discountFactor));
      else monthly = Math.round(base);
      return {
        monthly,
        bimonthly: Math.round(monthly * 2),
        quarterly: Math.round(monthly * 3),
        semiannual: Math.round(monthly * 6),
        yearly: Math.round(monthly * 12 * discountFactor),
      };
    }
    return { monthly: 0, bimonthly: 0, quarterly: 0, semiannual: 0, yearly: 0 };
  }, [renewalPrice, subscriptionPeriod, fallbackPlan]);

  // El precio especial del cliente tiene prioridad sobre el precio del
  // catálogo en el periodo para el que fue configurado (promociones).
  const prices = useMemo(() => {
    if (vipSpecial <= 0) return basePrices;
    return { ...basePrices, [vipKey]: Math.round(vipSpecial) };
  }, [basePrices, vipSpecial, vipKey]);

  // Compatibilidad hacia atrás (código legacy que usaba estos nombres):
  const monthlyPrice = prices.monthly;
  const annualPrice = prices.yearly;

  // Periodos a mostrar (metadata en @/lib/renewalPeriods). Ticket #274906: un cliente con
  // precio especial ve SOLO ese periodo + el anual; el resto ve todos los periodos con precio.
  const availablePeriods = useMemo(
    () => selectRenewalPeriods(prices, { vipSpecial, vipKey }),
    [prices, vipSpecial, vipKey]
  );

  // Si el periodo por defecto 'yearly' no tiene precio, elegir el primero disponible.
  useEffect(() => {
    if (availablePeriods.length === 0) return;
    if (!availablePeriods.find((p) => p.key === selectedPeriod)) {
      setSelectedPeriod(availablePeriods[availablePeriods.length - 1].key);
    }
  }, [availablePeriods, selectedPeriod]);

  // Porcentaje de ahorro CALCULADO dinámicamente a partir de los precios
  // efectivos (mensual x 12 vs anual) y redondeado al entero más cercano.
  // Fórmula pedida por el usuario:
  //   ((mensual * 12) - anual) / (mensual * 12) * 100
  const savingsPct = useMemo(() => {
    const base = monthlyPrice * 12;
    if (!base || !annualPrice) return 0;
    const pct = ((base - annualPrice) / base) * 100;
    return Math.max(0, Math.round(pct));
  }, [monthlyPrice, annualPrice]);

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getPlanName = () => {
    const names = {
      basic: 'Fit Evolution',
      basico: 'Fit Evolution',
      premium: 'Plan Premium',
      gold: 'Plan Gold VIP',
      vip_gold: 'Plan Gold VIP',
      competitivo: 'Plan Competitivo',
    };
    return names[planType?.toLowerCase()] || `Plan ${planType}`;
  };

  const getPeriodLabel = () => {
    const labels = {
      mensual: 'Mensual', monthly: 'Mensual',
      bimestral: 'Bimestral', bimonthly: 'Bimestral',
      trimestral: 'Trimestral', quarterly: 'Trimestral',
      semestral: 'Semestral', semester: 'Semestral',
      anual: 'Anual', yearly: 'Anual', annual: 'Anual',
    };
    return labels[subscriptionPeriod?.toLowerCase()] || subscriptionPeriod;
  };

  // ─── Handler unificado: navega al checkout con el periodo elegido ───
  const goPay = (e, overridePeriod = null) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const periodToUse = overridePeriod || selectedPeriod;
    // Monto del periodo elegido (antes bimestral/trimestral/semestral enviaban el mensual).
    const amountToUse = Number(prices[periodToUse]) || 0;

    // Navegar directo al checkout con el period elegido en el overlay.
    const params = new URLSearchParams();
    params.set('renewal', '1');
    if (planType) params.set('plan', planType);
    params.set('period', periodToUse);
    if (amountToUse) params.set('amount', String(amountToUse));
    if (vipSpecial > 0 && periodToUse === vipKey) params.set('vip', '1');
    navigate(`/checkout?${params.toString()}`);
  };

  const handleWhatsApp = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // wa.link oficial de Deisy — más confiable que wa.me con número
    window.open('https://wa.link/78phcb', '_blank', 'noopener,noreferrer');
  };

  // ═════ PERÍODO DE GRACIA (banner arriba, no bloquea) ═════
  if (graceDaysRemaining > 0 && !isFullyExpired) {
    return (
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-yellow-600 to-orange-600 text-white py-3 px-4 shadow-lg"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FaClock className="text-2xl animate-pulse" />
            <div>
              <p className="font-bold text-lg">
                Tu {getPlanName()} vence en {graceDaysRemaining} día{graceDaysRemaining > 1 ? 's' : ''}
              </p>
              <p className="text-sm text-yellow-100">
                {renewalPrice && <>Renueva por {formatPrice(renewalPrice)} ({getPeriodLabel()}) </>}
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={(e) => goPay(e, null)}
            data-testid="plan-expired-renew-grace-btn"
            className="bg-white text-orange-600 hover:bg-yellow-100 font-bold px-6"
          >
            RENOVAR AHORA <FaArrowRight className="ml-2" />
          </Button>
        </div>
      </motion.div>
    );
  }

  // ═════ PLAN COMPLETAMENTE VENCIDO (overlay bloqueante con selector 5 periodos) ═════
  if (isFullyExpired) {
    const hasAnyPrice = availablePeriods.length > 0;
    const selectedAmount = Number(prices[selectedPeriod]) || 0;
    const selectedMeta = PERIOD_META.find((p) => p.key === selectedPeriod) || PERIOD_META[0];
    const monthlyAnchor = Number(prices.monthly) || 0;

    return (
      <div
        className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        style={{ pointerEvents: 'auto' }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-2xl w-full bg-gradient-to-br from-gray-900 to-black rounded-3xl p-6 md:p-8 border border-pink-500/30 text-center relative my-8"
          style={{ zIndex: 51 }}
          data-testid="plan-expired-overlay-fullyexpired"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-pink-500/20 flex items-center justify-center">
            <FaLock className="text-4xl text-pink-500" />
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">
            Tu {getPlanName()} ha expirado
          </h2>
          <p className="text-gray-400 mb-6 text-sm">
            <FaHeart className="inline text-pink-500 mr-1" />
            Renueva para seguir con tu transformación. Elige tu suscripción:
          </p>

          {hasAnyPrice ? (
            <div className="space-y-2 mb-6 text-left" data-testid="renewal-periods-list">
              {availablePeriods.map((meta) => {
                const amount = Number(prices[meta.key]) || 0;
                const expected = monthlyAnchor * meta.months;
                const saves = expected > amount && expected > 0 ? expected - amount : 0;
                const pct = expected > 0 ? Math.round((saves / expected) * 100) : 0;
                const isSelected = selectedPeriod === meta.key;
                const isYearly = meta.key === 'yearly';
                return (
                  <button
                    key={meta.key}
                    type="button"
                    onClick={() => setSelectedPeriod(meta.key)}
                    data-testid={`renewal-period-${meta.key}`}
                    className={`relative w-full rounded-2xl p-4 transition-all duration-300 flex items-center gap-3 ${
                      isSelected ? 'scale-[1.01]' : 'opacity-90 hover:opacity-100'
                    }`}
                    style={{
                      background: isYearly
                        ? 'linear-gradient(135deg, rgba(var(--brand-primary-glow), 0.18), rgba(var(--brand-primary-glow), 0.06))'
                        : 'rgba(255,255,255,0.04)',
                      border: isSelected
                        ? '2px solid var(--brand-primary)'
                        : isYearly
                        ? '1.5px solid rgba(var(--brand-primary-glow), 0.5)'
                        : '1px solid rgba(255,255,255,0.12)',
                      boxShadow: isSelected && isYearly ? '0 0 20px rgba(var(--brand-primary-glow),0.2)' : 'none',
                    }}
                  >
                    {/* Radio icon */}
                    <div
                      className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                      style={{
                        borderColor: isSelected ? 'var(--brand-primary)' : 'rgba(255,255,255,0.3)',
                        background: isSelected ? 'var(--brand-primary)' : 'transparent',
                      }}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>

                    {/* Label + badge */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-bold text-white">{meta.label}</h3>
                        {isYearly && (
                          <span
                            className="text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1"
                            style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                          >
                            <FaCrown className="text-[9px]" /> Mejor oferta
                          </span>
                        )}
                        {pct > 0 && (
                          <span
                            className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                            style={{ background: 'rgba(34, 197, 94, 0.18)', color: '#4ADE80', border: '1px solid rgba(34, 197, 94, 0.35)' }}
                          >
                            Ahorra {pct}%
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">{meta.tag}</p>
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-black text-white leading-none">
                        {formatPrice(amount)}
                      </div>
                      {saves > 0 && (
                        <div className="text-[10px] text-gray-500 line-through mt-0.5">
                          {formatPrice(expected)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            renewalPrice && (
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30">
                <p className="text-gray-300 text-sm mb-2">Precio de renovación:</p>
                <p className="text-3xl font-bold text-white">
                  {formatPrice(renewalPrice)}
                  <span className="text-lg text-gray-400 ml-2">/ {getPeriodLabel()}</span>
                </p>
              </div>
            )
          )}

          {/* CTAs */}
          <div className="space-y-3 relative" style={{ zIndex: 52 }}>
            <Button
              type="button"
              onClick={(e) => goPay(e, null)}
              data-testid="plan-expired-pay-online-btn"
              className="w-full py-6 text-lg font-bold rounded-xl flex items-center justify-center gap-2 cursor-pointer"
              style={{
                background:
                  hasAnyPrice && selectedPeriod === 'yearly'
                    ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))'
                    : 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
                pointerEvents: 'auto',
              }}
            >
              <FaCreditCard />
              {hasAnyPrice
                ? `PAGAR PLAN ${selectedMeta.label.toUpperCase()} · ${formatPrice(selectedAmount)}`
                : 'PAGAR ONLINE'}
            </Button>

            <Button
              type="button"
              onClick={handleWhatsApp}
              variant="outline"
              data-testid="plan-expired-whatsapp-btn"
              className="w-full py-5 text-lg font-bold rounded-xl border-green-500 text-green-400 hover:bg-green-500/10 flex items-center justify-center gap-2 cursor-pointer"
              style={{ pointerEvents: 'auto' }}
            >
              <FaWhatsapp /> RENOVAR POR WHATSAPP
            </Button>
          </div>

          <p className="mt-4 text-sm text-gray-500">
            ¿Tienes dudas? Escríbenos por WhatsApp
          </p>
        </motion.div>
      </div>
    );
  }

  return null;
}
